import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, StatusBadge, Spinner } from "@/components/ui-parts";
import { UserPlus, X } from "lucide-react";

export function UsersPage() {
  const { user } = useAuth();
  const { activeTenantId, tenants } = useTenant();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | pending

  const load = () => {
    setLoading(true);
    api.get("/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [activeTenantId]);

  let filtered = user?.role === "superadmin" && activeTenantId && tab === "all"
    ? users.filter((u) => u.tenant_id === activeTenantId || u.role === "superadmin" || (tab === "pending" && !u.tenant_id))
    : users;
  if (tab === "pending") filtered = users.filter((u) => u.status === "pending");

  const pendingCount = users.filter((u) => u.status === "pending").length;

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/users", form);
      setForm({ name: "", email: "", password: "", role: "viewer" });
      setShowForm(false);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/users/${id}`, updates); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/users/${id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const tenantName = (tid) => tenants.find((t) => t.id === tid)?.name || (tid ? "—" : "Unassigned");

  return (
    <div className="p-8 max-w-[1600px]" data-testid="users-page">
      <PageHeader eyebrow="Administration" title="User Management"
        description="Kullanıcı yönetimi, tenant ataması ve onay iş akışları. Bekleyen kayıtlar SuperAdmin onayı gerektirir."
        actions={
          <>
            {user?.role === "superadmin" && (
              <>
                <button onClick={() => setTab("all")} className={`btn btn-sm ${tab === "all" ? "btn-primary" : "btn-secondary"}`} data-testid="tab-all">
                  All Users ({users.length})
                </button>
                <button onClick={() => setTab("pending")} className={`btn btn-sm ${tab === "pending" ? "btn-primary" : "btn-secondary"}`} data-testid="tab-pending">
                  Pending ({pendingCount})
                </button>
              </>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm((v) => !v)} data-testid="add-user-btn">
              <UserPlus size={12} /> Add User
            </button>
          </>
        }
      />

      {showForm && (
        <Section title="Create User Directly">
          <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-user-name" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-user-email" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Password</label>
              <input type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="new-user-password" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                <option value="viewer">Viewer</option>
                <option value="auditor">Auditor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button className="btn btn-primary" type="submit" data-testid="new-user-submit">Create</button>
            {err && <div className="col-span-full text-xs text-red-400">{err}</div>}
          </form>
        </Section>
      )}

      <Section>
        {loading ? <div className="p-8"><Spinner /></div> : (
          <table className="data-table">
            <thead><tr>
              <th>Name</th><th>Email</th><th>Requested / Role</th><th>Status</th>
              {user?.role === "superadmin" && <th>Tenant</th>}
              {tab === "pending" && <th>Company</th>}
              <th>Created</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="text-slate-100">{u.name}</td>
                  <td className="mono">{u.email}</td>
                  <td>
                    <span className={`badge ${u.role === "superadmin" ? "badge-brand" : u.role === "admin" ? "badge-info" : u.role === "auditor" ? "badge-medium" : "badge-low"}`}>{u.role}</span>
                  </td>
                  <td><StatusBadge status={u.status} /></td>
                  {user?.role === "superadmin" && (
                    <td>
                      {u.status === "pending" && !u.tenant_id ? (
                        <select
                          value=""
                          onChange={(e) => e.target.value && patch(u.id, { tenant_id: e.target.value })}
                          data-testid={`assign-tenant-${u.email}`}
                          style={{ minWidth: 140 }}
                        >
                          <option value="">— Assign tenant —</option>
                          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      ) : (
                        <span className="text-slate-300 text-xs">{tenantName(u.tenant_id)}</span>
                      )}
                    </td>
                  )}
                  {tab === "pending" && (
                    <td className="text-slate-300 text-xs">
                      <div>{u.company || "—"}</div>
                      {u.notes && <div className="text-[10px] text-slate-500 mt-0.5 italic">{u.notes}</div>}
                    </td>
                  )}
                  <td className="mono text-[10px] text-slate-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="flex gap-1 flex-wrap">
                    {u.role !== "superadmin" && (
                      <>
                        {u.status === "pending" && (!u.tenant_id ? (
                          <span className="text-[10px] text-amber-400 self-center">Tenant atayın</span>
                        ) : (
                          <button className="btn btn-primary btn-sm" onClick={() => patch(u.id, { status: "approved" })} data-testid={`approve-${u.email}`}>Approve</button>
                        ))}
                        {u.status !== "rejected" && u.status !== "pending" && (
                          <button className="btn btn-secondary btn-sm" onClick={() => patch(u.id, { status: "rejected" })}>Suspend</button>
                        )}
                        {u.status === "pending" && (
                          <button className="btn btn-secondary btn-sm" onClick={() => patch(u.id, { status: "rejected" })} data-testid={`reject-${u.email}`}>Reject</button>
                        )}
                        {u.status === "rejected" && (
                          <button className="btn btn-secondary btn-sm" onClick={() => patch(u.id, { status: "approved" })}>Reactivate</button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => del(u.id)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={user?.role === "superadmin" ? (tab === "pending" ? 8 : 7) : 6} className="text-center text-slate-500 py-6">Kullanıcı bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

export function TenantsPage() {
  const { tenants, loadTenants } = useTenant();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", sector: "General Enterprise", admin_name: "", admin_email: "", admin_password: "" });
  const [err, setErr] = useState("");

  const create = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/tenants", form);
      setForm({ name: "", slug: "", sector: "General Enterprise", admin_name: "", admin_email: "", admin_password: "" });
      setShowForm(false);
      loadTenants();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete tenant and ALL its data?")) return;
    try { await api.delete(`/tenants/${id}`); loadTenants(); } catch (e) { alert(formatApiError(e)); }
  };

  const toggleStatus = async (t) => {
    try { await api.patch(`/tenants/${t.id}`, { status: t.status === "active" ? "inactive" : "active" }); loadTenants(); } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-[1600px]" data-testid="tenants-page">
      <PageHeader eyebrow="Platform Administration" title="Tenant Management"
        description="Provision and manage customer workspaces. Each tenant has its own isolated GACRF assessment dataset."
        actions={<button className="btn btn-primary btn-sm" onClick={() => setShowForm((v) => !v)} data-testid="add-tenant-btn"><UserPlus size={12} /> New Tenant</button>}
      />

      {showForm && (
        <Section title="Provision New Tenant" description="Creates the tenant, seeds ~390 GACRF requirements and creates the first tenant admin user.">
          <form onSubmit={create} className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Company Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="tenant-name" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Slug (URL-friendly)</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required placeholder="acme-corp" data-testid="tenant-slug" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Sector</label>
              <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="tenant-sector">
                {["General Enterprise", "Financial Services", "Energy & Utilities", "Manufacturing", "Healthcare", "Government", "Telecommunications", "MSSP", "AI Enterprise"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Admin Name</label>
              <input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required data-testid="tenant-admin-name" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Admin Email</label>
              <input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required data-testid="tenant-admin-email" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Admin Password</label>
              <input type="password" minLength={8} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required data-testid="tenant-admin-password" />
            </div>
            <div className="md:col-span-3 flex gap-2 items-center">
              <button className="btn btn-primary" type="submit" data-testid="tenant-submit">Create Tenant</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              {err && <span className="text-xs text-red-400 ml-3">{err}</span>}
            </div>
          </form>
        </Section>
      )}

      <Section>
        <table className="data-table">
          <thead><tr>
            <th>Logo</th><th>Name</th><th>Slug</th><th>Sector</th><th>Status</th>
            <th>Users</th><th>Assessed</th><th>Created</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} data-testid={`tenant-row-${t.slug}`}>
                <td>
                  {t.logo_data_url ? (
                    <img src={t.logo_data_url} alt={t.name} style={{ height: 24, maxWidth: 60 }} />
                  ) : (
                    <div className="w-6 h-6 border border-slate-700 flex items-center justify-center text-[10px] text-slate-500">
                      {t.name.slice(0, 1)}
                    </div>
                  )}
                </td>
                <td className="text-slate-100 font-semibold">{t.name}</td>
                <td className="mono">{t.slug}</td>
                <td>{t.sector}</td>
                <td><StatusBadge status={t.status} /></td>
                <td className="mono">{t.user_count}</td>
                <td className="mono">{t.assessed_count}</td>
                <td className="mono text-[10px] text-slate-500">{t.created_at?.slice(0, 10)}</td>
                <td className="flex gap-1 flex-wrap">
                  <label className="btn btn-secondary btn-sm cursor-pointer">
                    Logo
                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                      const f = e.target.files[0]; if (!f) return;
                      if (f.size > 500 * 1024) { alert("Logo boyutu 500 KB'yi aşmamalı"); e.target.value = ""; return; }
                      const reader = new FileReader();
                      reader.onload = async () => {
                        try { await api.patch(`/tenants/${t.id}`, { logo_data_url: reader.result }); loadTenants(); }
                        catch (err) { alert(formatApiError(err)); }
                      };
                      reader.readAsDataURL(f);
                      e.target.value = "";
                    }} />
                  </label>
                  <button className="btn btn-secondary btn-sm" onClick={() => toggleStatus(t)}>{t.status === "active" ? "Deactivate" : "Activate"}</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(t.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

export function OrganizationSettingsPage() {
  const { withScope, activeTenantId } = useTenant();
  const [org, setOrg] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/organization", { params: withScope() }),
      api.get("/reference/frameworks"),
    ]).then(([o, f]) => {
      setOrg(o.data || {});
      setFrameworks(f.data);
    });
    // eslint-disable-next-line
  }, [activeTenantId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { tenant_id, _id, ...body } = org;
      await api.put("/settings/organization", body, { params: withScope() });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(formatApiError(e)); }
  };

  if (!org) return <div className="p-8"><Spinner /></div>;

  const set = (k, v) => setOrg({ ...org, [k]: v });
  const activeFrameworks = org.activeFrameworks || [];
  const toggleFw = (id) => {
    const next = activeFrameworks.includes(id)
      ? activeFrameworks.filter((x) => x !== id)
      : [...activeFrameworks, id];
    set("activeFrameworks", next);
    // Auto-set primary if not set
    if (next.length && !org.primaryFramework) set("primaryFramework", next[0]);
  };

  return (
    <div className="p-8 max-w-[1100px]" data-testid="org-page">
      <PageHeader eyebrow="Administration" title="Organization Settings"
        description="Assessment scope, reporting metadata, target certification level and active audit frameworks for this tenant." />
      <Section>
        <form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Organization Name</label>
            <input value={org.orgName || ""} onChange={(e) => set("orgName", e.target.value)} data-testid="org-name" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Business Unit</label>
            <input value={org.orgBusinessUnit || ""} onChange={(e) => set("orgBusinessUnit", e.target.value)} /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Assessment Scope</label>
            <input value={org.orgScope || ""} onChange={(e) => set("orgScope", e.target.value)} /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Sector</label>
            <select value={org.orgSector || "General Enterprise"} onChange={(e) => set("orgSector", e.target.value)}>
              {["General Enterprise", "Financial Services", "Energy & Utilities", "Manufacturing", "Healthcare", "Government", "Telecommunications", "MSSP", "AI Enterprise"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Assessor</label>
            <input value={org.orgAssessor || ""} onChange={(e) => set("orgAssessor", e.target.value)} /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Assessment Date</label>
            <input type="date" value={org.orgAssessmentDate || ""} onChange={(e) => set("orgAssessmentDate", e.target.value)} /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Target Certification</label>
            <select value={org.orgTargetCert || "Gold"} onChange={(e) => set("orgTargetCert", e.target.value)}>
              {["Bronze","Silver","Gold","Platinum"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Primary Framework</label>
            <select value={org.primaryFramework || ""} onChange={(e) => set("primaryFramework", e.target.value)} data-testid="org-primary-fw">
              <option value="">— Select —</option>
              {frameworks.filter((f) => activeFrameworks.includes(f.id)).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-2">
              Active Audit Frameworks <span className="text-slate-600 normal-case tracking-normal font-sans">— select which frameworks this tenant will audit against</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="org-frameworks">
              {frameworks.map((f) => {
                const isOn = activeFrameworks.includes(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleFw(f.id)}
                    data-testid={`fw-toggle-${f.id}`}
                    className={`text-left p-3 border transition-all ${isOn ? "bg-[#00E5FF]/10 border-[#00E5FF]/40" : "bg-[#0F172A] border-slate-800 hover:border-slate-700"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-xs font-semibold ${isOn ? "text-[#00E5FF]" : "text-slate-200"}`}>{f.name}</div>
                      <div className={`w-4 h-4 border ${isOn ? "bg-[#00E5FF] border-[#00E5FF]" : "border-slate-600"} flex items-center justify-center`}>
                        {isOn && <span className="text-[#050914] text-[10px] font-bold">✓</span>}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">{f.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2"><label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Notes</label>
            <textarea rows={4} value={org.orgNotes || ""} onChange={(e) => set("orgNotes", e.target.value)} /></div>
          <div className="md:col-span-2 flex gap-2 items-center">
            <button className="btn btn-primary" type="submit" data-testid="org-save">Save Settings</button>
            {saved && <span className="text-xs text-emerald-400">✓ Saved</span>}
          </div>
        </form>
      </Section>
    </div>
  );
}

export function AuditLogPage() {
  const { withScope, activeTenantId } = useTenant();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/audit-log", { params: withScope() }).then(({ data }) => setLogs(data)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [activeTenantId]);
  return (
    <div className="p-8 max-w-[1600px]" data-testid="audit-page">
      <PageHeader eyebrow="Administration" title="Audit Log"
        description="Immutable record of user actions across the platform. Retained server-side for compliance and forensics." />
      <Section>
        {loading ? <div className="p-8"><Spinner /></div> : (
          <div className="overflow-x-auto" style={{ maxHeight: "70vh" }}>
            <table className="data-table">
              <thead><tr><th>Time</th><th>User</th><th>Role</th><th>Action</th><th>Details</th></tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td className="mono text-[10px] text-slate-500">{new Date(l.timestamp).toLocaleString()}</td>
                    <td className="text-slate-200">{l.user_name} <span className="text-slate-500 text-[10px]">({l.user_email})</span></td>
                    <td><span className="badge badge-info">{l.role}</span></td>
                    <td className="mono text-[#00E5FF]">{l.action}</td>
                    <td className="text-slate-400 max-w-md">{l.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
